import { useState, useEffect, useRef } from 'react';
import mammoth from 'mammoth';
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  ImageRun,
  HeadingLevel,
} from 'docx';
import { FileText, Download } from 'lucide-react';
import { saveAs } from 'file-saver';
import { App } from 'antd';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

interface DocxEditorProps {
  file: File;
  onClose: () => void;
}

// 将 base64 转换为 Uint8Array
const base64ToUint8Array = (base64: string): Uint8Array => {
  const base64Data = base64.split(',')[1] || base64;
  const binaryString = window.atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
};

// 解析 HTML 并转换为 docx 段落
const parseHtmlToDocx = async (html: string): Promise<Paragraph[]> => {
  const paragraphs: Paragraph[] = [];
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // 处理所有元素，包括 img
  const elements = tempDiv.querySelectorAll(
    'p, h1, h2, h3, h4, h5, h6, ul, ol, li, img',
  );

  for (const el of elements) {
    const tagName = el.tagName.toLowerCase();

    // 处理图片
    if (tagName === 'img') {
      const img = el as HTMLImageElement;
      const src = img.src;

      if (src && src.startsWith('data:')) {
        try {
          const imageData = base64ToUint8Array(src);
          const mimeType = src.split(';')[0].split(':')[1];
          const imageExtension = mimeType.split('/')[1];

          paragraphs.push(
            new Paragraph({
              children: [
                new ImageRun({
                  data: imageData,
                  transformation: {
                    width: 500,
                    height: Math.round(
                      500 * (img.naturalHeight / img.naturalWidth) || 300,
                    ),
                  },
                  type: imageExtension as 'jpg' | 'png' | 'gif' | 'bmp',
                }),
              ],
              spacing: { before: 100, after: 100 },
            }),
          );
        } catch (error) {
          console.error('Image processing error:', error);
        }
      }
      continue;
    }

    const text = el.textContent || '';
    if (!text.trim()) continue;

    // 处理标题
    if (tagName.startsWith('h') && tagName.length === 2) {
      const level = parseInt(tagName[1]);
      let headingLevel = HeadingLevel.HEADING_1;
      if (level === 2) headingLevel = HeadingLevel.HEADING_2;
      else if (level === 3) headingLevel = HeadingLevel.HEADING_3;
      else if (level >= 4) headingLevel = HeadingLevel.HEADING_4;

      paragraphs.push(
        new Paragraph({
          text,
          heading: headingLevel,
          spacing: { before: 200, after: 100 },
        }),
      );
      continue;
    }

    // 处理列表项
    if (tagName === 'li') {
      paragraphs.push(
        new Paragraph({
          text: '• ' + text,
          spacing: { before: 60, after: 60 },
          indent: { left: 360 },
        }),
      );
      continue;
    }

    // 处理普通段落
    if (tagName === 'p') {
      const children: (TextRun | ImageRun)[] = [];
      let currentText = '';

      for (const node of el.childNodes) {
        if (node.nodeType === Node.TEXT_NODE) {
          currentText += node.textContent;
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as HTMLElement;
          const tag = element.tagName.toLowerCase();

          // 处理段落内的图片
          if (tag === 'img') {
            if (currentText) {
              children.push(new TextRun({ text: currentText }));
              currentText = '';
            }

            const img = element as HTMLImageElement;
            const src = img.src;
            if (src && src.startsWith('data:')) {
              try {
                const imageData = base64ToUint8Array(src);
                const mimeType = src.split(';')[0].split(':')[1];
                const imageExtension = mimeType.split('/')[1];

                children.push(
                  new ImageRun({
                    data: imageData,
                    transformation: {
                      width: 500,
                      height: Math.round(
                        500 * (img.naturalHeight / img.naturalWidth) || 300,
                      ),
                    },
                    type: imageExtension as 'jpg' | 'png' | 'gif' | 'bmp',
                  }),
                );
              } catch (error) {
                console.error('Image processing error:', error);
              }
            }
            continue;
          }

          // 先添加之前的文本
          if (currentText) {
            children.push(new TextRun({ text: currentText }));
            currentText = '';
          }

          // 添加带样式的文本
          children.push(
            new TextRun({
              text: element.textContent || '',
              bold: tag === 'strong' || tag === 'b',
              italics: tag === 'em' || tag === 'i',
              underline: tag === 'u' ? {} : undefined,
            }),
          );
        }
      }

      // 添加剩余的文本
      if (currentText) {
        children.push(new TextRun({ text: currentText }));
      }

      if (children.length > 0) {
        paragraphs.push(
          new Paragraph({
            children,
            spacing: { before: 60, after: 60 },
          }),
        );
      }
    }
  }

  // 如果没有段落，添加一个空段落
  if (paragraphs.length === 0) {
    paragraphs.push(new Paragraph({ text: '' }));
  }

  return paragraphs;
};

export default function DocxEditor({ file }: DocxEditorProps) {
  const { message } = App.useApp();
  const [htmlContent, setHtmlContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);

  // 读取 DOCX 文件并转换为 HTML
  useEffect(() => {
    const loadDocx = async () => {
      try {
        const arrayBuffer = await file.arrayBuffer();

        const result = await mammoth.convertToHtml(
          { arrayBuffer },
          {
            styleMap: [
              "p[style-name='Heading 1'] => h1:fresh",
              "p[style-name='Heading 2'] => h2:fresh",
              "p[style-name='Heading 3'] => h3:fresh",
              'b => strong',
              'i => em',
            ],
            convertImage: mammoth.images.imgElement(function (image) {
              return image.read('base64').then(function (imageBuffer) {
                return {
                  src: 'data:' + image.contentType + ';base64,' + imageBuffer,
                };
              });
            }),
          },
        );

        setHtmlContent(result.value);
        setLoading(false);
      } catch {
        message.error('文件读取失败');
        setLoading(false);
      }
    };
    loadDocx();
  }, [file, message]);

  // 初始化 Quill 编辑器
  useEffect(() => {
    if (loading || !editorRef.current) return;

    // 如果已经初始化，只更新内容
    if (quillRef.current) {
      quillRef.current.root.innerHTML = htmlContent;
      return;
    }

    // 清空容器，防止重复初始化
    editorRef.current.innerHTML = '';

    const quill = new Quill(editorRef.current, {
      theme: 'snow',
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['clean'],
        ],
      },
    });

    quill.root.innerHTML = htmlContent;
    quillRef.current = quill;

    // 组件卸载时清理
    return () => {
      if (quillRef.current) {
        quillRef.current = null;
      }
    };
  }, [loading, htmlContent]);

  // 导出 TXT
  const exportText = () => {
    const currentHtml = quillRef.current?.root.innerHTML || htmlContent;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = currentHtml;
    const text = tempDiv.textContent || '';

    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, file.name.replace(/\.docx$/i, '_edited.txt'));
    message.success('TXT 文件已下载');
  };

  // 导出 MD
  const exportMarkdown = () => {
    const currentHtml = quillRef.current?.root.innerHTML || htmlContent;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = currentHtml;
    const text = tempDiv.textContent || '';

    const blob = new Blob([text], { type: 'text/markdown;charset=utf-8' });
    saveAs(blob, file.name.replace(/\.docx$/i, '_edited.md'));
    message.success('Markdown 文件已下载');
  };

  // 导出 DOCX
  const exportDocx = async () => {
    try {
      setExporting(true);
      message.loading('正在生成 DOCX...', 0);

      const currentHtml = quillRef.current?.root.innerHTML || htmlContent;
      const paragraphs = await parseHtmlToDocx(currentHtml);

      const doc = new Document({
        sections: [
          {
            properties: {},
            children: paragraphs,
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, file.name.replace(/\.docx$/i, '_edited.docx'));

      message.destroy();
      message.success('DOCX 文件已下载');
    } catch (error) {
      message.destroy();
      message.error('DOCX 生成失败');
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        <span className="ml-3 text-slate-400">正在加载...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* 工具栏 */}
      <div className="flex items-center justify-end mb-4 pb-4 border-b border-gray-700 flex-shrink-0">
        <div className="flex items-center gap-2">
          <button
            onClick={exportText}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 text-slate-300 rounded-xl hover:bg-slate-800 transition-all cursor-pointer border border-slate-700 hover:border-amber-500/50 hover:text-amber-400"
          >
            <FileText className="w-4 h-4" />
            导出 TXT
          </button>
          <button
            onClick={exportMarkdown}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 text-slate-300 rounded-xl hover:bg-slate-800 transition-all cursor-pointer border border-slate-700 hover:border-amber-500/50 hover:text-amber-400"
          >
            <FileText className="w-4 h-4" />
            导出 MD
          </button>
          <button
            onClick={exportDocx}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 disabled:from-slate-700 disabled:to-slate-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white text-sm font-semibold shadow-lg shadow-amber-500/30 transition-all cursor-pointer"
          >
            <Download className="w-4 h-4" />
            {exporting ? '导出中...' : '导出 DOCX'}
          </button>
        </div>
      </div>

      {/* 编辑器 */}
      <div className="flex-1 overflow-hidden bg-white rounded-xl border border-slate-700 docx-quill-editor">
        <div ref={editorRef} className="h-full" />
      </div>

      <style>{`
        .docx-quill-editor { display: flex; flex-direction: column; }
        .docx-quill-editor .ql-toolbar {
          background: #1f2937;
          border-color: #374151;
          border-radius: 12px 12px 0 0;
        }
        .docx-quill-editor .ql-container {
          flex: 1;
          border-radius: 0 0 12px 12px;
          background: #fff;
        }
        .docx-quill-editor .ql-editor {
          font-family: 'SimSun', serif;
          font-size: 14px;
        }
      `}</style>
    </div>
  );
}
